/**
 * fog_sky — the fog's colour FROM THE SKY and the valley mist (2026-09-25,
 * realism §8 R5). Pure; AtmosphereModule applies it, the
 * `verify:lighting-physical` harness pins it.
 *
 * The colour a distant surface fades into is the in-scattered light of the
 * air, which is what the sky at the horizon IS: the Hillaire horizon ring
 * (azimuth-averaged — the sun's glow is the height fog's own lobe) at the exposure
 * curve's scale. Under a deck the horizon greys toward the CIE overcast
 * horizon (Lz/3); a thick fog (≥ ~0.004/m) turns toward the diffuse light's
 * own grey — fog is lit by the whole sky, not by the blue ring.
 */
import { CLEAR_AIR_VISIBILITY_KM } from './atmosphere.js';

export interface FogSkyInputs {
  /** Horizon radiance per unit E0: azimuth average and toward the sun. */
  horizon: { x: number; y: number; z: number };
  horizonSun: { x: number; y: number; z: number };
  /** E0 in scene units (E0_LUX × luxToScene). */
  e0Scene: number;
  /** Deck cloudiness 0..1 and the CIE overcast horizon radiance (scene units). */
  cloudiness: number;
  overcastHorizon: number;
  /** The weather's fog density (1/m) and the diffuse light's radiance (scene units). */
  fogDensity: number;
  diffuseRadiance: number;
}

/**
 * The clear-air haze where no aerial perspective runs: the SKY'S OWN visibility (2026-09-27 — lib/sky/atmosphere's
 * 40 km, the aerosol column the dome and the key light are made of), so the no-froxel tiers fade distance into the
 * horizon at the same range the froxel tiers do. Was a constant 0.0012 (a 1.6 km visibility) — a fog on every clear day.
 */
export const CLEAR_AIR_HAZE = 1.978 / (CLEAR_AIR_VISIBILITY_KM * 1000);

/**
 * The live-ops `weather_profile` rows' fog densities AS SEEDED (tools/asset-pipeline/seed.mjs §6 — the pre-visibility-law
 * FogExp2 numbers: 'clear' 0.0015/m is a 1.3 km visibility). sky/post (2026-09-27): AtmosphereModule took
 * max(resolved, row) — so after THE VISIBILITY LAW re-specced the resolver, the seeded 'clear' row put a 1.3 km fog
 * back on every clear day (the hub's pinned clear noon read FogExp2 0.00145/m, 1.4 km). A row EQUAL to its seed is
 * now neutral (the resolver's visibility table owns the weather's fog — its 'fog' / rain / snow / dust terms), and a
 * live-ops row set ABOVE its seed adds its excess — the sky_lighting_profile rule (LightingModule's SEED_PROFILE).
 */
export const SEED_PROFILE_FOG: Readonly<Record<string, number>> = { clear: 0.0015, cloudy: 0.004, rain: 0.008, storm: 0.014, fog: 0.025, snow: 0.006 };
/** A live-ops weather row's fog as an EXCESS over its seed (0 for a seeded row, the whole density for an unknown kind). */
export function profileFogExcess(kind: string, rowDensity: number): number {
  const seed = SEED_PROFILE_FOG[kind] ?? 0;
  return Number.isFinite(rowDensity) ? Math.max(0, rowDensity - seed) : 0;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (e0: number, e1: number, v: number): number => { const t = clamp01((v - e0) / (e1 - e0)); return t * t * (3 - 2 * t); };

/** The fog colour (linear scene radiance RGB). */
export function fogSkyColor(i: FogSkyInputs): [number, number, number] {
  // The ISOTROPIC ring (lighting, 2026-09-26; was ¼ toward the sun): one fog colour meets the dome at every azimuth, and
  // the sun's glow is the height-fog pass's HG lobe. Measured at the far tier's silhouette (fog ÷ dome at 1°, sun 10°):
  // ¼ sun side 1.45× beside the sun and 1.23× away from it — the bright band; the plain ring 1.18× / 1.01×; at noon
  // both ≤ 1.1× in every direction. Toward a low sun the fog now sits under the glow (a darker skyline, as at dusk).
  const w = 0;
  const hx = (i.horizon.x * (1 - w) + i.horizonSun.x * w) * i.e0Scene;
  const hy = (i.horizon.y * (1 - w) + i.horizonSun.y * w) * i.e0Scene;
  const hz = (i.horizon.z * (1 - w) + i.horizonSun.z * w) * i.e0Scene;
  const c = clamp01(i.cloudiness);
  const oc = [i.overcastHorizon * 0.93, i.overcastHorizon * 0.96, i.overcastHorizon];
  let r = hx + (oc[0] - hx) * c, g = hy + (oc[1] - hy) * c, b = hz + (oc[2] - hz) * c;
  const thick = smooth(0.004, 0.012, i.fogDensity);
  const d = i.diffuseRadiance;
  r += (d * 0.95 - r) * thick; g += (d * 0.97 - g) * thick; b += (d - b) * thick;
  return [Math.max(0, r), Math.max(0, g), Math.max(0, b)];
}

/**
 * The valley mist 0..1: saturated air (T − T_dew under ~3 °C) around dawn and
 * the evening, or wet ground after rain, in calm air (wind blows mist away).
 */
export function mistAmount(spreadC: number, tod: number, wetness: number, wind01: number): number {
  const saturated = smooth(3, 0, spreadC); // 1 when the air sits at its dew point
  const dawn = Math.exp(-Math.pow((tod - 0.24) / 0.08, 2));
  const evening = 0.45 * Math.exp(-Math.pow((tod - 0.82) / 0.07, 2));
  const timeW = Math.max(dawn, evening, 0.25);
  const afterRain = smooth(0.35, 0.8, wetness) * 0.6;
  const calm = 1 - 0.8 * clamp01(wind01);
  return clamp01(Math.max(saturated * timeW, afterRain) * calm);
}

/**
 * The analytic height fog's density for a tier base and the mist — THE VISIBILITY LAW for the ground layer
 * (sky/post P2, 2026-09-27). The pass is Beer-Lambert in distance (OD = ρ·d over a ~20 m layer at the terrain floor),
 * so a density IS a visibility: V = 3.912 / ρ. The old day base (0.35 × the tier's 0.010–0.012 = 0.0035–0.0042/m) was
 * a 1 km visibility at eye height on every clear noon: far ground buried in a white band, and — the NOON LOOK — the
 * pass mixed its horizon-ring colour (5–6× the upper sky's radiance) into every upward sky pixel at OD ≈ 0.07/sin θ,
 * lifting and greying the dome 40–50 % at 30–60°. Now the layer is written in visibility: a dry clear day's layer is a
 * faint HEIGHT_FOG_DAY_KM (the sky's own aerosol carries the distance haze), a saturated calm dawn the tier's full
 * mist (the tier density × 1.3 — 250–300 m), log-interpolated by the mist.
 */
export const HEIGHT_FOG_DAY_KM = 20;
export function heightFogDensity(tierDensity: number, mist: number): number {
  const day = 3.912 / (HEIGHT_FOG_DAY_KM * 1000);
  const full = Math.max(day, tierDensity * 1.3);
  return day * Math.pow(full / day, clamp01(mist));
}

/**
 * THE NIGHT FOG (the night-sky realism wave, 2026-10-06 — agent A). By day the air's colour is the Hillaire horizon; at
 * night that is black, and the air takes the NIGHT sky's light instead (`sky_sampler.nightSkySample`, absolute cd/m²,
 * the natural sky at the shader's display lift): a thin clear haze is the colour of the horizon behind it — the ring's
 * azimuthal MEDIAN (a far town's dome stands BEHIND the haze: the hills are silhouetted against it, as in Soltesz's
 * Flagstaff dome) — and a thick fog is lit from every side: the natural sky above it and, over a town, its lamps from
 * among and below it — A FOG IS A DECK ON THE GROUND (`thickArt` = the artificial hemisphere under a full deck,
 * OVERCAST_AMPLIFICATION × the glow). So a misty night in a city glows amber (sodium light scattered by the droplets),
 * a misty night in the wilds is a dark blue-grey veil. Weighted by the SAME `thick` ramp the day colour uses. Returns
 * cd/m² RGB; the caller multiplies by the night factor (the lamps are on) and converts through
 * `photometry.emissiveNits` (THE PHYSICAL-EMISSIVE CONTRACT).
 */
export function fogNightColor(ringMedian: { x: number; y: number; z: number }, thickNat: { x: number; y: number; z: number }, thickArt: { x: number; y: number; z: number }, fogDensity: number): [number, number, number] {
  const thick = fogThickness(fogDensity);
  const f = (a: number, n: number, ar: number): number => Math.max(0, a + (n + ar - a) * thick);
  return [f(ringMedian.x, thickNat.x, thickArt.x), f(ringMedian.y, thickNat.y, thickArt.y), f(ringMedian.z, thickNat.z, thickArt.z)];
}
/** How thick a fog reads (0 clear haze → 1 a fog lit from every side) — the day and the night colours share it. */
export function fogThickness(fogDensity: number): number { return smooth(0.004, 0.012, Number.isFinite(fogDensity) ? fogDensity : 0); }

/**
 * THE SKY'S SHARE of the height fog (engine VolumetricFogPass `skyOpacity`, 2026-10-06): the fog's opacity over SKY
 * pixels as a fraction of its opacity over geometry. By day 1 (the pass as it was). On a clear night 0 — the sky shader
 * draws its own airmass extinction and horizon brightening, so a valley layer the camera stands in never paints the fog
 * colour over the stars. A thick fog veils the sky again (`thick`). Under a RAINING deck the sky is SkyModule's lit rain
 * dome (THE LIT RAIN DECK — the town's deck added to the overcast zenith), so rain needs no term here.
 */
export function skyFogShare(nightFactor: number, thick: number): number {
  const nf = Number.isFinite(nightFactor) ? clamp01(nightFactor) : 0;
  if (nf <= 0) return 1;
  return 1 - nf * (1 - (Number.isFinite(thick) ? clamp01(thick) : 0));
}
